<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    :disabled="isSubmitBtnDisabled"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      {{ formTitle }}
    </template>

    <template #card-notes>
      <div class="card-notes">
        <p>
          Use this form to add or update a loved one or trusted person who you authorize to manage your account and to receive notifications
          about your rides.
        </p>
      </div>
    </template>

    <template #content>
      <div
        v-if="!isFamilyDashboard"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Customer Phone Number* </label>
        <phone-number-input
          v-model="phoneNumber"
          class="col-sm-7"
          :page-id="pageId"
          :required="true"
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Prefix </label>
        <div class="col-sm-7">
          <sanitized-input
            id="prefix"
            v-model="familyMember.prefix"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> First Name* </label>
        <div class="col-sm-7">
          <sanitized-input
            id="firstName"
            v-model="familyMember.firstName"
            :required="!familyMember.lastName"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Last Name* </label>
        <div class="col-sm-7">
          <sanitized-input
            id="lastName"
            v-model="familyMember.lastName"
            :required="!familyMember.firstName"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Family Phone Number* </label>
        <phone-number-input
          id="family-member-phone"
          v-model="familyMember.phoneNumber"
          class="col-sm-7"
          :page-id="pageId"
          :required="true"
        />
      </div>

      <div class="row align-items-center">
        <el-checkbox
          id="transactionalSms"
          v-model="familyMember.transactionalSms"
          class="offset-sm-3 pl-10"
        >
          Do not receive text updates
        </el-checkbox>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Email </label>
        <div class="col-sm-7">
          <email-input
            v-model="familyMember.email"
            @update-validation="updateEmailValidation"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Relationship </label>
        <div class="col-sm-7">
          <dropdown-list
            v-model="familyMember.relationship"
            :options="familyRelationList"
            filterable
          />
        </div>
      </div>

      <div
        v-if="isOtherReasonFieldShow"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Please Specify* </label>
        <div class="col-sm-7">
          <sanitized-input
            id="otherRelation"
            v-model="familyMember.otherRelationship"
            required
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>

    <template #additional-buttons>
      <p-button
        v-if="isFamilyDashboard"
        type="yellow"
        round
        @click="cancelChanges"
      >
        Cancel
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { FamilyRelationship } from '@/models/lovedOne';
import { familyRelationList } from '@/constants/lovedOne';
import { ListType } from '@/models/app';
import { LovedOneInput } from '@/models/lovedOne';
import { lovedOneService } from '@/services/lovedOne';
import { callerService } from '@/services/caller';

const pageId = 'add-family-member';

const route = useRoute();
const router = useRouter();

const { customerInfo, customerPhoneNumber } = useCustomer();
const { isFamilyDashboard } = useDashboard();

const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { lastFormData, setFormData } = useGlobal(pageId);

const familyMember: Ref<
  LovedOneInput & {
    otherRelationship?: string;
  }
> = ref({
  prefix: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  transactionalSms: true,
  email: '',
  relationship: familyRelationList[0].key,
});
const familyId = ref('');
const phoneNumber = ref('');
const callerId = ref('');
const callerEmail = ref('');
const isEmailValid = ref(true);
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
});

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));
const isFamilyPhoneNumberValid = computed(() => util.isPhoneNumberValid(familyMember.value.phoneNumber));
const isOtherReasonFieldShow = computed(() => familyMember.value.relationship === FamilyRelationship.Other);
const formTitle = computed(() => (familyId.value ? 'Update Family Member' : 'Add Family Member'));
const submitBtnText = computed(() => (familyId.value ? 'Update Family Member' : 'Add Family Member'));
const isSubmitBtnDisabled = computed(
  () => !isPhoneNumberValid.value || !isFamilyPhoneNumberValid.value || !isEmailValid.value || status.isLoading,
);

const getFamilyMember = async () => {
  status.spinnerMessage = 'Loading Family Info...';
  status.isLoading = true;
  try {
    const lovedOne = await lovedOneService.fetchLovedOne({
      id: familyId.value,
      isIncludeCallers: true,
    });

    const associatedCaller = lovedOne?.associatedCallers?.find((caller: any) => caller.id === customerInfo.value.id);

    familyMember.value = {
      prefix: lovedOne!.prefix ?? undefined,
      firstName: lovedOne!.firstName!,
      lastName: lovedOne!.lastName!,
      phoneNumber: lovedOne!.phoneNumber!,
      transactionalSms: lovedOne!.transactionalSms!,
      email: lovedOne!.email!,
      relationship: associatedCaller.relationship,
    };

    const relationship = familyRelationList.find((relation: ListType) => relation.key === familyMember.value.relationship);

    if (!relationship) {
      familyMember.value.otherRelationship = familyMember.value.relationship ?? '';
      familyMember.value.relationship = FamilyRelationship.Other;
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const checkFamilyEmailAvailable = () => {
  if (!familyMember.value.email) {
    return true;
  }

  if (familyMember.value.email === callerEmail.value) {
    showErrorPopupDialog({
      text: `Family member email can\'t be same as customer email (${callerEmail.value}).`,
    });

    return false;
  }

  return true;
};

const getCustomerInfo = async () => {
  callerId.value = '';
  callerEmail.value = '';
  status.spinnerMessage = 'Loading Customer Info...';
  status.isLoading = true;

  try {
    const response = await callerService.fetchCustomerInfo({
      phoneNumber: phoneNumber.value,
    });

    callerEmail.value = response.email ?? '';
    callerId.value = response.id;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const doAddLovedOne = async (data: LovedOneInput) => {
  status.spinnerMessage = 'Adding Family Member...';
  try {
    await lovedOneService.addLovedOne({
      callerId: callerId.value,
      data,
    });

    await showSuccessPopupDialog({
      text: 'The family member has been added successfully.',
    });

    if (isFamilyDashboard.value) {
      router.push({
        name: 'FamilyMembers',
      });
    } else if (customerInfo.value?.id === callerId.value) {
      events.emit('refresh-right-panel');
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const doUpdateLovedOne = async (data: LovedOneInput) => {
  status.spinnerMessage = 'Updating Family Member...';
  try {
    await lovedOneService.updateLovedOne({
      id: familyId.value,
      data,
    });

    await showSuccessPopupDialog({
      text: 'The family member has been updated successfully.',
    });

    router.push({
      name: 'FamilyMembers',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const submitForm = async () => {
  if (!isFamilyDashboard.value) {
    await getCustomerInfo();
  }

  const isFamilyEmailAvailable = checkFamilyEmailAvailable();
  if (!callerId.value || !isFamilyEmailAvailable) {
    return;
  }

  const postData = {
    prefix: familyMember.value.prefix,
    firstName: familyMember.value.firstName,
    lastName: familyMember.value.lastName,
    phoneNumber: familyMember.value.phoneNumber,
    transactionalSms: familyMember.value.transactionalSms,
    email: familyMember.value.email,
    relationship:
      familyMember.value.relationship === FamilyRelationship.Other
        ? familyMember.value.otherRelationship || ''
        : familyMember.value.relationship,
  };

  status.isLoading = true;
  if (!familyId.value) {
    // Add Family Member
    await doAddLovedOne(postData);
  } else {
    // Update Family Member
    await doUpdateLovedOne(postData);
  }
  status.isLoading = false;
};

const cancelChanges = async () => {
  const isDiscardChangesConfirmed = await showConfirmPopupDialog({
    title: 'Discard any changes made?',
    text: 'Discard',
  });

  if (!isDiscardChangesConfirmed) {
    return;
  }

  router.push({
    name: 'FamilyMembers',
  });
};

const resetForm = () => {
  if (!isFamilyDashboard.value) {
    phoneNumber.value = customerPhoneNumber.value || '';
    callerId.value = '';
  }

  familyMember.value = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    transactionalSms: true,
    email: '',
    relationship: familyRelationList[0].key,
  };
};

const validateCustomerInfo = () => {
  if (!customerInfo.value?.id) {
    router.push({
      name: 'Profile',
    });
  }
};

const prefillDataWithCustomerInfo = () => {
  callerId.value = customerInfo.value.id;
  callerEmail.value = customerInfo.value.email;
  phoneNumber.value = customerInfo.value.phoneNumber;
};

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

watch(customerInfo, resetForm);

onMounted(() => {
  if (isFamilyDashboard.value) {
    util.moveToTop();

    validateCustomerInfo();
    prefillDataWithCustomerInfo();

    familyId.value = (route.query?.id as string) || '';
    if (familyId.value) {
      getFamilyMember();
    }

    return;
  }

  familyMember.value = {
    firstName: lastFormData.value?.firstName || '',
    lastName: lastFormData.value?.lastName || '',
    phoneNumber: lastFormData.value?.phoneNumber || '',
    transactionalSms: lastFormData.value?.transactionalSms || true,
    email: lastFormData.value?.email || '',
    relationship: lastFormData.value?.relationship || familyRelationList[0].key,
  };
  phoneNumber.value = lastFormData.value?.callerPhoneNumber || customerPhoneNumber.value;
});

onBeforeUnmount(() => {
  setFormData({
    ...familyMember.value,
    callerPhoneNumber: phoneNumber.value,
  });
});
</script>
